import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Star, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";

const POPPED_KEY = "n8_popped_pro_grants";

// Small notifications tray that lives in the header. Bell shows a soft terracotta
// dot when there are unseen items; click opens a floating list of the most recent
// notifications with relative timestamps.
export default function NotificationsTray() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);
  const subCtx = useSubscription();

  const load = useCallback(async () => {
    try {
      const { data } = await endpoints.notifications(false);
      setItems(data);

      // Pop a persistent Sonner toast the FIRST time we see a
      // ``pro_granted`` notification. We remember which grants we've
      // already popped in localStorage so refreshes don't nag.
      let popped = new Set();
      try { popped = new Set(JSON.parse(localStorage.getItem(POPPED_KEY) || "[]")); } catch {}
      const fresh = (data || []).filter(
        (n) => n.kind === "pro_granted" && !popped.has(n.id),
      );
      if (fresh.length) {
        for (const n of fresh) {
          toast.success(n.title, {
            description: n.body,
            duration: 8000,
            icon: <Sparkles size={16} />,
          });
          popped.add(n.id);
        }
        localStorage.setItem(POPPED_KEY, JSON.stringify(Array.from(popped)));
        // Refresh entitlement snapshot so the tier chip updates immediately.
        try { await subCtx.refresh(); } catch {}
      }
    } catch { /* silent */ }
  }, [subCtx]);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  // Close panel on outside click / escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unseen = items.filter((n) => !n.seen).length;

  const openItem = async (n) => {
    if (!n.seen) {
      try {
        await endpoints.markNotificationSeen(n.id);
      } catch { /* silent */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, seen: true } : x)));
    }
    setOpen(false);
    nav("/community");
  };

  const markAll = async () => {
    try {
      await endpoints.markAllNotificationsSeen();
    } catch { /* silent */ }
    setItems((prev) => prev.map((x) => ({ ...x, seen: true })));
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        data-testid="notifications-bell"
        className="relative p-2 rounded-full hover:bg-[hsl(var(--sand))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
      >
        <Bell size={16} strokeWidth={1.6} />
        {unseen > 0 && (
          <span
            data-testid="notifications-dot"
            className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[hsl(var(--terracotta))] ring-2 ring-[hsl(var(--paper))]"
            aria-hidden
          />
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          data-testid="notifications-tray"
          className="absolute right-0 mt-2 w-[min(360px,92vw)] rounded-md border hairline bg-[hsl(var(--paper))] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] z-50"
        >
          <div className="px-4 py-3 flex items-center justify-between gap-3 border-b hairline">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Recent cheers</div>
            {unseen > 0 && (
              <button
                onClick={markAll}
                data-testid="notifications-mark-all"
                className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] inline-flex items-center gap-1"
              >
                <Check size={11} /> Mark all seen
              </button>
            )}
          </div>

          <ul className="max-h-[380px] overflow-auto divide-y hairline" data-testid="notifications-list">
            {items.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="notifications-empty">
                No cheers yet. Share a milestone to invite them.
              </li>
            )}
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => openItem(n)}
                  data-testid={`notification-${n.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-[hsl(var(--sand))]/40 tab-link ${
                    !n.seen ? "bg-[hsl(var(--terracotta))]/6" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 mt-0.5 w-7 h-7 rounded-full grid place-items-center ${
                      !n.seen ? "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]" : "bg-[hsl(var(--sand))]/60 text-[hsl(var(--muted-foreground))]"
                    }`}>
                      <Star size={12} fill={!n.seen ? "currentColor" : "none"} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight">{n.title || "Celebrating You"}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 leading-snug">{n.body}</div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))] mt-1">
                        {relativeTime(n.created_at)}
                        {n.milestone_ref?.category_name && (
                          <> · {n.milestone_ref.category_name}</>
                        )}
                      </div>
                    </div>
                    {!n.seen && <span className="mt-1 w-2 h-2 rounded-full bg-[hsl(var(--terracotta))] shrink-0" aria-hidden />}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
